import { defineStore } from "pinia";
import { BaseItemTypes } from "@/components/dnd/ItemTypes";
import { BaseListItem } from "@/components/common/base-list/interfaces/ListTypes";

/**
 * История последних открытых элементов аналитики для кнопки "Вернуться".
 *
 * Хранится в sessionStorage: она своя у каждой вкладки, переживает перезагрузку и
 * пересоздание фрейма внутри вкладки и чистится вместе с кешем браузера. localStorage
 * не подходит, он общий на все вкладки.
 */

const HISTORY_STORAGE_KEY = "analytics.history";
const HISTORY_DEPTH = 3;

export interface AnalyticsHistoryEntry {
  // сегмент списка, то есть rootStore.generalMode: у отчётов это 900, а не DestType
  mode: number;
  elType: BaseItemTypes;
  Id: number;
  SrcType?: number;
  DestType: number;
  ElementId?: number;
  Name: string;
}

export type AnalyticsHistoryState = {
  entries: AnalyticsHistoryEntry[];
  hydrated: boolean;
  /**
   * Элемент, к которому идёт возврат: он уже лежит последним в истории, поэтому писать
   * его повторно не нужно. Держим саму цель, а не флаг: если возврат не состоялся и
   * пришёл другой элемент, он запишется как обычно, и ничего не залипнет.
   */
  pendingBack?: AnalyticsHistoryEntry;
};

function sameEntry(a: AnalyticsHistoryEntry, b: AnalyticsHistoryEntry): boolean {
  return a.mode === b.mode
    && a.elType === b.elType
    && a.Id === b.Id
    && a.DestType === b.DestType
    && (a.SrcType ?? null) === (b.SrcType ?? null)
    && (a.ElementId ?? null) === (b.ElementId ?? null);
}

export const analyticsHistoryStore = defineStore({
  id: "analyticsHistoryStore",
  state: () =>
    ({
      entries: [],
      hydrated: false,
      pendingBack: undefined,
    } as AnalyticsHistoryState),

  actions: {
    hydrate() {
      if (this.hydrated) return;
      this.hydrated = true;
      if (typeof window === "undefined") return;
      try {
        const raw = window.sessionStorage.getItem(HISTORY_STORAGE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          this.entries = parsed
            .filter((el: any) => el && typeof el.Id === "number")
            .slice(-HISTORY_DEPTH);
        }
      } catch (e) {
        // битое значение не должно ломать запуск, начинаем с пустой истории
        this.entries = [];
      }
    },

    persist() {
      if (typeof window === "undefined") return;
      try {
        window.sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(this.entries));
      } catch (e) {
        console.log(JSON.stringify(e));
      }
    },

    /** Запомнить открытый элемент. Последняя запись это текущий элемент. */
    push(element: BaseListItem, mode: number) {
      this.hydrate();

      // папки не открывают таблицу, возвращаться к ним нечем. При смене сегмента выбор
      // сначала обнуляется, такие пустые проходы тоже отсекаются здесь
      if (!element || !element.Id || element.elType === BaseItemTypes.FOLDER) return;

      const entry: AnalyticsHistoryEntry = {
        mode,
        elType: element.elType,
        Id: element.Id,
        SrcType: element.SrcType,
        DestType: element.DestType,
        ElementId: element.ElementId,
        Name: element.Name || "",
      };

      const backTarget = this.pendingBack;
      this.pendingBack = undefined;
      if (backTarget && sameEntry(backTarget, entry)) return;

      const last = this.entries[this.entries.length - 1];
      if (last && sameEntry(last, entry)) return;

      this.entries.push(entry);
      if (this.entries.length > HISTORY_DEPTH) {
        this.entries.splice(0, this.entries.length - HISTORY_DEPTH);
      }
      this.persist();
    },

    /** Снять текущую запись и отдать предыдущую, к которой возвращаемся. */
    takePrevious(): AnalyticsHistoryEntry | undefined {
      this.hydrate();
      if (this.entries.length < 2) return undefined;

      this.entries.pop();
      this.persist();

      const target = this.entries[this.entries.length - 1];
      this.pendingBack = target;
      return target;
    },

    clear() {
      this.entries = [];
      this.pendingBack = undefined;
      this.persist();
    },
  },

  getters: {
    canGoBack(state): boolean {
      return state.entries.length > 1;
    },

    previous(state): AnalyticsHistoryEntry | undefined {
      return state.entries.length > 1 ? state.entries[state.entries.length - 2] : undefined;
    },
  },
})
